import { EventState } from '@bp/planung-graphql-types';
import {
  ActionBar,
  Checkbox,
  EmptyState,
  Grid,
  GridColumn,
  GridRow,
  Input,
  TeacherIllustration,
} from '@bp/ui-components';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { SubstitutionDataHighlightEntry } from '../../../pages/Substitutions/Plan/PlanSubstitutions/PlanSubstitutions';
import { SubstitutionDataEntry } from '../../../pages/Substitutions/Plan/PlanSubstitutions/utils';
import {
  use_SchoolEventsQuery,
  use_SetSubstitutionMutation,
  useAbsencesQuery,
  useAttendancePlanEventsQuery,
  useTeachersSubstitutionsQuery,
} from '../../../types/planung-graphql-client-defs';
import { getMinutesDifference, normalizedDate } from '../../../utils/dateCalculations';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { SubstitutionRoomsSelect } from '../components/SubstitutionRoomsSelect';
import { useSubstitutionStore } from '../SubstitutionProvider';
import { SubstitutionEventsTableType } from '../tables/SubstitutionEventsTable';
import styles from './SubstitutionForm.module.scss';
import {
  SubstitutionFormRow,
  SubstitutionFormRowEventType,
  SubstitutionFormRowStatus,
  SubstitutionFormRowType,
} from './SubstitutionFormRow';

type SubstitutionFormProps = {
  timetableStart: Dayjs;
  timetableEnd: Dayjs;
  currentEvent: SubstitutionEventsTableType;
  teacherEvents: Map<string, SubstitutionEventsTableType[]>;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export type SubstitutionFormType = {
  roomUuids: string[];
  comment: string;
  substitutionRows: SubstitutionFormRowType[];
};

export const SubstitutionForm = ({
  timetableStart,
  timetableEnd,
  currentEvent,
  teacherEvents,
  onLoad,
  onClose,
}: SubstitutionFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
  const { selectedDay } = useSubstitutionStore();
  const eventContext = useMemorizedCacheTag('EVENT');
  const absenceContext = useMemorizedCacheTag('ABSENCES');

  const selectedDayStartString = selectedDay.startOf('day').startOf('hour').utc().toISOString();
  const selectedDayEndString = selectedDay.endOf('day').endOf('hour').utc().toISOString();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [layoutLoaded, setLayoutLoaded] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [showAvailable, setShowAvailable] = useState<boolean>(false);
  const [showStandby, setShowStandby] = useState<boolean>(false);

  const [, setSubstitution] = use_SetSubstitutionMutation();

  const [{ data: attendancePlanEventsData }] = useAttendancePlanEventsQuery({
    variables: {
      organizationUuid: organizationUuid,
      day: selectedDay.format('YYYY-MM-DD'),
    },
  });

  const { teacherData } = useLoadBasicData({ pause: false });

  const [{ data: absencesData }] = useAbsencesQuery({
    variables: {
      where: {
        start_LT: selectedDayEndString,
        end_GT: selectedDayStartString,
      },
    },
    context: absenceContext,
    pause: !selectedDay,
  });

  const [{ data: schoolEventsData }] = use_SchoolEventsQuery({
    variables: {
      organizationUuid: organizationUuid,
      from: selectedDay.startOf('day').format('YYYY-MM-DD'),
      to: selectedDay.endOf('day').format('YYYY-MM-DD'),
    },
    context: eventContext,
  });

  const [{ data: teachersSubstitutionsData }] = useTeachersSubstitutionsQuery({
    variables: {
      organizationUuid,
      schoolYearUuid: currentSchoolYear?.uuid ?? '',
      subjectUuid: currentEvent.subject.uuid ?? '',
      classUuids: currentEvent.classes.map((c) => c.uuid) ?? [],
    },
    pause: !currentSchoolYear,
    context: eventContext,
  });

  const getName = useCallback(
    (status: SubstitutionFormRowStatus) => {
      switch (status) {
        case 'normal':
          return t('substitutions.normalLesson');
        case 'attendance':
          return t('substitutions.attendance');
        case 'cancellation-school-event':
          return t('substitutions.cancellationSchoolEvent');
        case 'substitution':
          return t('substitutions.title', { count: 1 });
        case 'other':
          return t('common.other');
        case 'on-school-event':
          return t('substitutions.onSchoolEvent');
        case 'signed-out':
          return t('substitutions.signedOut');
        default:
          return t('substitutions.normalLesson');
      }
    },
    [t],
  );

  const hourRef = useRef<HTMLDivElement>(null);
  const [hourWidth, setHourWidth] = useState<number>(0);

  const startHour = dayjs(timetableStart).hour();
  const endHour = dayjs(timetableEnd).hour();
  const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) =>
    dayjs()
      .set('hour', startHour + i)
      .startOf('hour'),
  );

  const rows: SubstitutionFormRowType[] = useMemo(() => {
    const rows: SubstitutionFormRowType[] = [];

    // iterate teachers of school
    teacherData?.people
      .filter((t) => t.active)
      .forEach((teacher) => {
        const teacherSubstitutionData = teachersSubstitutionsData?.teachersSubstitutions.find(
          (t) => t.teacherUuid === teacher.uuid,
        );

        // "Fächer"
        const _teacherEvents: SubstitutionFormRowEventType[] = [];
        // farbiger Hintergrund
        const teacherHighlights: SubstitutionDataHighlightEntry[] = [];

        let disabled: boolean = false;
        let givenSubstituteHours: number | null = null;

        // iterate events and calculate teacherEvents
        const teacherEventsForCurrentTeacher = teacherEvents.get(teacher.uuid) || [];
        for (const event of teacherEventsForCurrentTeacher) {
          const eventTeacher = event.teachers.find((t) => t.uuid === teacher.uuid);
          const missingTeacher = event.missingTeachers.find((t) => t.uuid === teacher.uuid);
          const substituteTeacher = event.substitutionTeachers.find((t) => t.uuid === teacher.uuid);

          const teacherEvent = {
            eventUuid: event.uuid,
            start: event.start,
            end: event.end,
            subjectShortName: event.subject.shortName,
            subjectName: event.subject.name,
            classOrGroupNames: event.originalGroupClassNames,
          };

          const substituteTeacherStatus = event.doNotSubstitute ? 'other' : substituteTeacher?.status || 'normal';
          const missingTeacherStatus = event.doNotSubstitute ? 'other' : missingTeacher?.status || 'signed-out';
          const eventTeacherStatus = event.doNotSubstitute
            ? 'other'
            : event.onSchoolTrip
              ? 'cancellation-school-event'
              : eventTeacher?.status || 'normal';

          if (substituteTeacher) {
            givenSubstituteHours =
              event.uuid === currentEvent.uuid ? substituteTeacher.givenSubstituteHours : givenSubstituteHours;
            _teacherEvents.push({
              ...teacherEvent,
              name: getName(substituteTeacherStatus),
              status: substituteTeacherStatus,
            });
          } else if (missingTeacher && missingTeacher.state !== EventState.PersonOnSchoolTrip) {
            _teacherEvents.push({
              ...teacherEvent,
              name: getName(missingTeacherStatus),
              status: missingTeacherStatus,
            });
          } else if (eventTeacher) {
            givenSubstituteHours =
              event.uuid === currentEvent.uuid ? eventTeacher.givenSubstituteHours : givenSubstituteHours;
            _teacherEvents.push({
              ...teacherEvent,
              name: getName(eventTeacherStatus),
              status: eventTeacherStatus,
            });
          }
        }

        // iterate school events with teachers to highlight the background as 'on-school-event' (Auf Klassenfahrt)
        schoolEventsData?.schoolEvents
          .filter((schoolEvent) => schoolEvent.persons.some((n) => n.uuid === teacher.uuid))
          .forEach((schoolEvent) => {
            const schoolEventStart = dayjs(schoolEvent.start);
            const schoolEventEnd = dayjs(schoolEvent.end);

            const fullDay =
              schoolEventStart.isSameOrBefore(timetableStart) && schoolEventEnd.isSameOrAfter(timetableEnd);

            const inTimegrid =
              schoolEventStart.isBetween(timetableStart, timetableEnd) ||
              schoolEventEnd.isBetween(timetableStart, timetableEnd);

            const eventsOverlap =
              schoolEventStart.isBefore(currentEvent.end) && schoolEventEnd.isAfter(currentEvent.start);

            const status = 'on-school-event';

            if (fullDay || eventsOverlap) disabled = true;

            teacherHighlights.push({
              highlightUuid: schoolEvent.uuid,
              highlightName: getName(status) + (schoolEvent.comment ? ` (${schoolEvent.comment})` : ''),
              teacherUuid: teacher.uuid,
              teacherName: teacher.displayName,
              status: status,
              start: schoolEventStart,
              end: schoolEventEnd,
              renderStart: schoolEventStart.isSameOrBefore(timetableStart)
                ? dayjs(selectedDay).set('hour', startHour)
                : schoolEventStart,
              renderEnd: schoolEventEnd.isSameOrAfter(timetableEnd)
                ? dayjs(selectedDay).set('hour', endHour + 1)
                : schoolEventEnd,
              fullDay: fullDay,
              inTimegrid: inTimegrid,
              groupClassNames: schoolEvent.groupClassNames,
            });
          });

        // iterate absences to highlight the background as 'signed-out' (Abgemeldet)
        absencesData?.absences
          .filter((a) => a.person.uuid === teacher.uuid)
          .forEach((absence) => {
            const absenceStart = dayjs(absence.start);
            const absenceEnd = dayjs(absence.end);

            const fullDay =
              dayjs(absenceStart).isSameOrBefore(timetableStart) && dayjs(absenceEnd).isSameOrAfter(timetableEnd);

            const inTimegrid =
              dayjs(absenceStart).isBetween(timetableStart, timetableEnd) ||
              dayjs(absenceEnd).isBetween(timetableStart, timetableEnd);

            const eventsOverlap = absenceStart.isBefore(currentEvent.end) && absenceEnd.isAfter(currentEvent.start);

            const status = 'signed-out';

            if (fullDay || eventsOverlap) disabled = true;

            teacherHighlights.push({
              highlightUuid: absence.uuid,
              highlightName: getName(status),
              teacherUuid: teacher.uuid,
              teacherName: teacher.displayName,
              status: status,
              start: absenceStart,
              end: absenceEnd,
              renderStart: dayjs(absenceStart).isSameOrBefore(timetableStart)
                ? dayjs(selectedDay).set('hour', startHour)
                : absenceStart,
              renderEnd: dayjs(absenceEnd).isSameOrAfter(timetableEnd)
                ? dayjs(selectedDay).set('hour', endHour + 1)
                : absenceEnd,
              fullDay: fullDay,
              inTimegrid: inTimegrid,
            });
          });

        attendancePlanEventsData?.attendancePlanEvents
          ?.filter((a) => a.persons.some((p) => p.uuid === teacher.uuid))
          ?.forEach((attendancePlan) => {
            const start = dayjs(attendancePlan.start);
            const end = dayjs(attendancePlan.end);
            const inTimegrid =
              dayjs(start).isBetween(timetableStart, timetableEnd) ||
              dayjs(end).isBetween(timetableStart, timetableEnd);

            teacherHighlights.push({
              highlightUuid: attendancePlan.uuid,
              highlightName: attendancePlan.subject.name,
              teacherUuid: teacher.uuid,
              teacherName: teacher.displayName,
              status: 'attendance',
              start: start,
              end: end,
              renderStart: dayjs(start).isSameOrBefore(timetableStart)
                ? dayjs(selectedDay).set('hour', startHour)
                : start,
              renderEnd: dayjs(end).isSameOrAfter(timetableEnd) ? dayjs(selectedDay).set('hour', endHour + 1) : end,
              fullDay: false,
              inTimegrid: inTimegrid,
              groupClassNames: attendancePlan.class.name,
            });
          });

        rows.push({
          teacherUuid: teacher.uuid,
          isOriginalEventTeacher: !!currentEvent.originalTeachers.find((t) => t.uuid === teacher.uuid),
          teacherName: teacher.listName,
          selected: !!currentEvent.teachers.find((t) => t.uuid === teacher.uuid),
          givenSubstituteHours: givenSubstituteHours,
          target: teacherSubstitutionData?.substituteHours ?? 0,
          current: teacherSubstitutionData?.givenSubstituteHours ?? 0,
          sameClass: teacherSubstitutionData?.sameClass ?? false,
          sameSubject: teacherSubstitutionData?.sameSubject ?? false,
          events: _teacherEvents,
          highlights: teacherHighlights,
          disabled: disabled,
        });
      });

    return rows;
  }, [
    teacherData?.people,
    teachersSubstitutionsData?.teachersSubstitutions,
    schoolEventsData?.schoolEvents,
    absencesData?.absences,
    attendancePlanEventsData?.attendancePlanEvents,
    currentEvent,
    teacherEvents,
    getName,
    timetableStart,
    timetableEnd,
    selectedDay,
    startHour,
    endHour,
  ]);

  const initialValues: SubstitutionFormType = {
    roomUuids: currentEvent.rooms.map((r) => r.uuid) || [],
    comment: currentEvent.substitutionComment || '',
    substitutionRows: rows,
  };

  const frames: { left: number; width: number }[] = [];

  const normalizedEventStart = normalizedDate(currentEvent.start ?? dayjs(), selectedDay);
  const normalizedEventEnd = normalizedDate(currentEvent.end ?? dayjs(), selectedDay);

  const isBefore = dayjs(normalizedEventStart).isSameOrBefore(timetableStart, 'minute');
  const isAfter = dayjs(normalizedEventEnd).isSameOrAfter(timetableEnd, 'minute');

  const subjectDuration = getMinutesDifference(normalizedEventStart, normalizedEventEnd);
  const subjectWidth = (hourWidth / 60) * subjectDuration;

  const startDuration = isBefore ? 0 : getMinutesDifference(normalizedEventStart, timetableStart);
  const startWidth = (hourWidth / 60) * startDuration;

  const endDuration = isAfter ? 0 : getMinutesDifference(normalizedEventEnd, timetableEnd);
  const endWidth = (hourWidth / 60) * endDuration;

  frames.push(
    {
      left: 0,
      width: startWidth,
    },
    {
      left: startWidth + subjectWidth,
      width: endWidth,
    },
  );

  async function handleSubmit(values: SubstitutionFormType, formHelpers: FormikHelpers<SubstitutionFormType>) {
    onLoad(true);

    const selectedTeachers = values.substitutionRows.filter((r) => r.selected);
    await setSubstitution(
      {
        eventUuid: currentEvent.uuid,
        rooms: values.roomUuids,
        coveringTeachers: selectedTeachers.map((t) => {
          return {
            teacherUuid: t.teacherUuid,
            givenSubstituteHours: t.givenSubstituteHours,
          };
        }),
        substitutionComment: values.comment,
      },
      eventContext,
    );

    onLoad(false);
    formHelpers.resetForm();
    onClose();
  }

  useEffect(() => {
    const handleResize = () => {
      if (hourRef.current) {
        setHourWidth(hourRef.current.getBoundingClientRect().width);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    setLayoutLoaded(true);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ setFieldValue, values, isSubmitting, dirty, resetForm, errors }) => {
        const filteredRowTeacherUuids = new Set<string>(
          (values.substitutionRows ?? [])
            .filter((r) => {
              const matchesSearch = r.teacherName.toLowerCase().includes(search.toLowerCase());
              if (!showAvailable && !showStandby) return matchesSearch;

              // check if the teacher has any event or highlight in the selected timeframe
              const hasEventInTimeframe = r.events.some(
                (event) =>
                  dayjs(event.start).isBefore(currentEvent.end) && dayjs(event.end).isAfter(currentEvent.start),
              );

              const hasHighlightInTimeframe = r.highlights
                .filter((h) => h.status !== 'attendance')
                .some(
                  (highlight) =>
                    dayjs(highlight.start).isBefore(currentEvent.end) &&
                    dayjs(highlight.end).isAfter(currentEvent.start),
                );

              // check if the teacher has attendance events in the timeframe (for standby filter)
              const hasAttendanceInTimeframe = r.highlights.some(
                (highlight) =>
                  dayjs(highlight.start).isBefore(currentEvent.end) &&
                  dayjs(highlight.end).isAfter(currentEvent.start) &&
                  highlight.status === 'attendance',
              );

              if (showAvailable && showStandby) {
                // show teachers who are either available OR have attendance events
                return matchesSearch && !hasEventInTimeframe && !hasHighlightInTimeframe && hasAttendanceInTimeframe;
              } else if (showAvailable) {
                // only include teachers without any event or highlight in the timeframe
                return matchesSearch && !hasEventInTimeframe && !hasHighlightInTimeframe;
              } else if (showStandby) {
                // only include teachers with attendance events in the timeframe
                return matchesSearch && hasAttendanceInTimeframe;
              }

              return matchesSearch;
            })
            .map((r) => r.teacherUuid),
        );
        return (
          <Form className={styles['substitution-form']}>
            <div className={styles.info}>
              <div>
                {t('substitutions.initialTeacher')}:
                <span className={styles.bold}>
                  {currentEvent.originalTeachers && currentEvent.originalTeachers.length > 0
                    ? currentEvent.originalTeachers.map((t) => t.name).join(', ')
                    : t('common.none')}
                </span>
              </div>
              <div>
                {t('substitutions.initialRooms')}:
                <span className={styles.bold}>
                  {currentEvent.originalRooms && currentEvent.originalRooms.length > 0
                    ? currentEvent.originalRooms.map((r) => r.name).join(', ')
                    : t('common.none')}
                </span>
              </div>
            </div>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <GridColumn width={6}>
                  <SubstitutionRoomsSelect
                    originalRoomUuids={currentEvent.originalRooms.map((r) => r.uuid) ?? []}
                    eventRoomUuids={values.roomUuids}
                    eventStart={currentEvent.start ?? dayjs()}
                    eventEnd={currentEvent.end ?? dayjs()}
                    onChange={async (uuids) => await setFieldValue('roomUuids', uuids)}
                    error={errors.roomUuids as string}
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <Input
                    name='comment'
                    value={values.comment}
                    label={t('common.comment')}
                    onChange={async (e) => {
                      await setFieldValue('comment', e.target.value);
                    }}
                    error={errors.comment}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ActionBar
              marginBottom='0'
              isOnWhite
              onGlobalFilterChange={(value) => setSearch(value)}
              globalFilterValue={search}
              extendedActionsLeft={
                <>
                  <Checkbox
                    name='show-available'
                    onChange={(e) => setShowAvailable(e.target.checked)}
                    checked={showAvailable}
                    label={t('substitutions.showAvailableTeachers')}
                  />
                  <Checkbox
                    name='show-standby'
                    onChange={(e) => setShowStandby(e.target.checked)}
                    checked={showStandby}
                    label={t('substitutions.showStandbyTeachers')}
                  />
                </>
              }
            />

            <div className={styles.substitutions}>
              <div className={styles.header}>
                <div className={styles.teachers}>
                  <div className={styles.tiny}></div>
                  <div className={styles.big}>{t('persons.title', { count: 1 })}</div>
                  <div className={styles.small}>{t('common.value')}</div>
                  <div className={styles.tiny}>{t('substitutions.target')}</div>
                  <div className={styles.tiny}>{t('substitutions.current')}</div>
                  <div className={styles.medium}>{t('classes.title', { count: 1 })}</div>
                  <div className={styles.medium}>{t('subject.title', { count: 1 })}</div>
                </div>
                <div className={styles.hours} style={{ gridTemplateColumns: `repeat(${hours.length}, 1fr)` }}>
                  {hours.map((hour, index) => {
                    return (
                      <div
                        key={hour.toString()}
                        ref={index === 0 ? hourRef : undefined}
                        className={styles.hour}
                      >{`${String(hour.hour()).padStart(2, '0')}:00`}</div>
                    );
                  })}
                </div>
              </div>

              <div className={classNames(styles.rows, 'has-scrollbar')} style={{ opacity: !layoutLoaded ? 0 : 1 }}>
                {filteredRowTeacherUuids.size > 0 ? (
                  // loop over  values.substitutionRows to keep index
                  values.substitutionRows.map((substitutionRow, index) => {
                    if (filteredRowTeacherUuids.has(substitutionRow.teacherUuid)) {
                      return (
                        <SubstitutionFormRow
                          key={substitutionRow.teacherUuid}
                          hours={hours}
                          frames={frames}
                          hourWidth={hourWidth}
                          row={substitutionRow}
                          index={index}
                        />
                      );
                    }
                    return <></>;
                  })
                ) : (
                  <EmptyState
                    icon={<TeacherIllustration />}
                    iconColor='var(--color-grey-light)'
                    title={t('substitutions.noTeachers')}
                    forcedHeight='calc(100vh - 600px)'
                  />
                )}
              </div>
            </div>
            <div className={styles.footer}>
              <div className={styles.legend}>
                <div className={styles.colors}>
                  <div>{getName('normal')}</div>
                  <div>{getName('attendance')}</div>
                  <div>{getName('cancellation-school-event')}</div>
                  <div>{getName('substitution')}</div>
                  <div>{getName('other')}</div>
                  <div>{getName('on-school-event')}</div>
                  <div>{getName('signed-out')}</div>
                </div>
              </div>

              <ModalBottomButtons
                closeButton={{
                  callback: () => {
                    onClose();
                    resetForm();
                  },
                }}
                submitButton={{
                  disabled: isSubmitting || !dirty,
                }}
                className={styles.bottom}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
